import { createFeatureSelector, createSelector } from '@ngrx/store';
import { StudentRegistrationState } from './student-registration.state';

export const selectStudentRegistrationState = createFeatureSelector<StudentRegistrationState>('studentRegistration');

// Basic Details Selectors
export const selectBasicDetails = createSelector(selectStudentRegistrationState, (state) => state.basicDetails);

export const selectBasicDetailsCourseId = createSelector(selectBasicDetails, (basicDetails) => basicDetails.courseId);

export const selectBasicDetailsName = createSelector(selectBasicDetails, (basicDetails) => basicDetails.name);

export const selectBasicDetailsEmail = createSelector(selectBasicDetails, (basicDetails) => basicDetails.email);

// Educational Details Selectors
export const selectEducationalDetails = createSelector(
    selectStudentRegistrationState,
    (state) => state.educationalDetails,
);

export const selectQualifications = createSelector(
    selectEducationalDetails,
    (educationalDetails) => educationalDetails.qualifications,
);

export const selectQualificationsCount = createSelector(
    selectQualifications,
    (qualifications) => qualifications.length,
);

// Professional Details Selectors
export const selectProfessionalDetails = createSelector(
    selectStudentRegistrationState,
    (state) => state.professionalDetails,
);

// Step Navigation Selectors
export const selectCurrentStep = createSelector(selectStudentRegistrationState, (state) => state.currentStep);

export const selectCompletedSteps = createSelector(selectStudentRegistrationState, (state) => state.completedSteps);

export const selectIsStepCompleted = (step: number) =>
    createSelector(selectCompletedSteps, (completedSteps) => completedSteps.includes(step));

export const selectIsStepValid = (step: number) =>
    createSelector(selectCompletedSteps, (completedSteps) => completedSteps.includes(step - 1) || step < 2);

// Form State Selectors
export const selectIsSubmitted = createSelector(selectStudentRegistrationState, (state) => state.isSubmitted);

export const selectIsLoading = createSelector(selectStudentRegistrationState, (state) => state.isLoading);

export const selectError = createSelector(selectStudentRegistrationState, (state) => state.error);

// Combined Selectors
export const selectFormData = createSelector(
    selectBasicDetails,
    selectEducationalDetails,
    selectProfessionalDetails,
    (basicDetails, educationalDetails, professionalDetails) => ({
        basicDetails,
        educationalDetails,
        professionalDetails,
    }),
);

export const selectStepProgress = createSelector(
    selectCurrentStep,
    selectCompletedSteps,
    (currentStep, completedSteps) => ({
        currentStep,
        completedSteps,
        totalSteps: 4,
    }),
);

export const selectCanProceedToStep = (step: number) =>
    createSelector(selectCompletedSteps, (completedSteps) => {
        if (step === 1) return true;
        return completedSteps.includes(step - 1);
    });

export const selectFormValidation = createSelector(
    selectBasicDetails,
    selectEducationalDetails,
    selectProfessionalDetails,
    (basicDetails, educationalDetails, professionalDetails) => {
        const basicDetailsValid = !!(
            basicDetails.name &&
            basicDetails.email &&
            basicDetails.phone &&
            basicDetails.gender &&
            basicDetails.address
        );
        const educationalDetailsValid =
            educationalDetails.qualifications.length > 0 &&
            educationalDetails.qualifications.every(
                (q) => q.highestEducation && q.fieldOfStudy && q.institutionName && q.yearOfCompletion && q.certificate,
            );
        const professionalDetailsValid = !!(
            professionalDetails.currentOrganisation &&
            professionalDetails.designation &&
            professionalDetails.workExperience &&
            professionalDetails.document
        );

        return {
            basicDetailsValid,
            educationalDetailsValid,
            professionalDetailsValid,
            allValid: basicDetailsValid && educationalDetailsValid && professionalDetailsValid,
        };
    },
);
